import { useEffect, useState } from "react";
import { Box, HStack, SimpleGrid, Spinner, Text, VStack } from "@chakra-ui/react";
import { HiChatBubbleLeftRight, HiTableCells, HiTag } from "react-icons/hi2";
import dayjs from "dayjs";
import { LeadService } from "@/services/LeadService";
import InfoField from "../InfoField";
import { extractInteractionData } from "./leadCollectedDataHelpers";

const LeadDataTab = ({ lead }) => {
    const [interactions, setInteractions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        setIsLoading(true);
        LeadService.getInteractions(lead.id, 1, 100)
            .then((data) => setInteractions(data?.items ?? []))
            .finally(() => setIsLoading(false));
    }, [lead.id]);

    const additionalEntries = Object.entries(lead.additionalData ?? {});
    const campaignProducts = lead.campaignProducts ?? [];

    const conversationsWithData = interactions
        .map((interaction) => ({ interaction, ...extractInteractionData(interaction.collectedAnswers) }))
        .filter(({ displayAnswers, displayQA, displayDocs }) => displayAnswers.length > 0 || displayQA.length > 0 || displayDocs.length > 0);

    return (
        <VStack gap={4} align="stretch">
            <Box bg="bg.card" border="1px solid" borderColor="border.primary" borderRadius={12} p={5}>
                <HStack gap={3} mb={4}>
                    <Box p={2} bg="bg.secondary" borderRadius={8} color="brand.400"><HiTag /></Box>
                    <Text fontWeight={700} color="white">Atrybuty kampanii</Text>
                </HStack>
                <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                    <InfoField label="Data przejęcia">
                        <Text color="white" fontSize="sm">{dayjs(lead.createdAt).format("DD.MM.YYYY HH:mm")}</Text>
                    </InfoField>
                    <InfoField label="Produkty kampanii">
                        {campaignProducts.length > 0 ? (
                            <VStack align="flex-start" gap={1}>
                                {campaignProducts.map((p) => (
                                    <Text key={p.id} color="white" fontSize="sm">{p.name}</Text>
                                ))}
                            </VStack>
                        ) : (
                            <Text color="text.grey" fontSize="sm">
                                {lead.campaignId ? "Kampania nie ma zdefiniowanych produktów" : "Lead nie jest przypisany do kampanii"}
                            </Text>
                        )}
                    </InfoField>
                </SimpleGrid>
            </Box>

            <Box bg="bg.card" border="1px solid" borderColor="border.primary" borderRadius={12} p={5}>
                <HStack gap={3} mb={4}>
                    <Box p={2} bg="bg.secondary" borderRadius={8} color="brand.400"><HiTableCells /></Box>
                    <Text fontWeight={700} color="white">Dodatkowe informacje</Text>
                </HStack>
                {additionalEntries.length > 0 ? (
                    <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                        {additionalEntries.map(([key, value]) => (
                            <InfoField key={key} label={key}>
                                <Text color="white" fontSize="sm">{value}</Text>
                            </InfoField>
                        ))}
                    </SimpleGrid>
                ) : (
                    <Text color="text.grey" fontSize="sm">Brak dodatkowych informacji</Text>
                )}
            </Box>

            <Box bg="bg.card" border="1px solid" borderColor="border.primary" borderRadius={12} p={5}>
                <HStack gap={3} mb={4}>
                    <Box p={2} bg="bg.secondary" borderRadius={8} color="brand.400"><HiChatBubbleLeftRight /></Box>
                    <Text fontWeight={700} color="white">Zebrane dane</Text>
                </HStack>
                {isLoading ? (
                    <VStack py={6} align="center"><Spinner color="brand.400" size="sm" /></VStack>
                ) : conversationsWithData.length === 0 ? (
                    <Text color="text.grey" fontSize="sm">Brak zebranych danych z rozmów</Text>
                ) : (
                    <VStack align="stretch" gap={3}>
                        {conversationsWithData.map(({ interaction, displayAnswers, displayQA, displayDocs }) => (
                            <Box
                                key={interaction.id}
                                bg="bg.secondary" border="1px solid" borderColor="border.primary" borderRadius={8} p={4}
                            >
                                <Text color="text.grey" fontSize="xs" mb={3}>
                                    {dayjs(interaction.createdAt).format("DD.MM.YYYY HH:mm")}
                                </Text>

                                {displayAnswers.length > 0 && (
                                    <Box mb={displayQA.length > 0 || displayDocs.length > 0 ? 4 : 0}>
                                        <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
                                            {displayAnswers.map((a) => (
                                                <InfoField key={a.name} label={a.display_name}>
                                                    <Text color="white" fontSize="sm">{a.value}</Text>
                                                </InfoField>
                                            ))}
                                        </SimpleGrid>
                                    </Box>
                                )}

                                {displayQA.length > 0 && (
                                    <Box mb={displayDocs.length > 0 ? 4 : 0} bg="bg.card" border="1px solid" borderColor="border.primary" borderRadius={8} overflow="hidden">
                                        {displayQA.map((item, i) => (
                                            <Box
                                                key={item.name} px={4} py={3}
                                                borderBottom={i < displayQA.length - 1 ? "1px solid" : "none"} borderColor="border.primary"
                                            >
                                                <Text color="text.grey" fontSize="sm" mb={1}>{item.name}</Text>
                                                <Text color="white" fontSize="sm" fontWeight={500}>{item.value}</Text>
                                            </Box>
                                        ))}
                                    </Box>
                                )}

                                {displayDocs.length > 0 && (
                                    <Box bg="bg.card" border="1px solid" borderColor="border.primary" borderRadius={8} overflow="hidden">
                                        {displayDocs.map((item, i) => (
                                            <Box
                                                key={item.name} px={4} py={3}
                                                borderBottom={i < displayDocs.length - 1 ? "1px solid" : "none"} borderColor="border.primary"
                                            >
                                                <Text color="text.grey" fontSize="sm" mb={1}>{item.name}</Text>
                                                <Text color="white" fontSize="sm" fontWeight={500}>{item.value}</Text>
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        ))}
                    </VStack>
                )}
            </Box>
        </VStack>
    );
};

export default LeadDataTab;
